import { Layout } from "antd";
import OrderFulfillment from "../pages/order-fulfillment/OrderFulfillment";
import { Routes, Route, Outlet, useLocation, useNavigate } from "react-router-dom";
import MaterialInward from "../pages/material-inward/MaterialInward";
import * as Styles from "./styles";
import User from "./icons/user.svg";
import { Button, Dropdown } from "antd";
import { ReactComponent as OrderIcon } from "./icons/order.svg";
import { ReactComponent as MaterialIcon } from "./icons/material.svg";
import { ReactComponent as MaterialPopupIcon } from "./icons/material-popup.svg";
import { ReactComponent as DecommissionIcon } from "./icons/decommission.svg";
import { format } from "date-fns";
import MaterialOrderDetail from "../pages/material-topup/components/drop-bin-details/DropBinDetails";
import { Navigate } from "react-router-dom";
import MaterialTopup from "../pages/material-topup/MaterialTopup";
import TaskId from "../pages/order-fulfillment/components/task-id/TaskId";
import OrderId from "../pages/order-fulfillment/components/order-id/OrderId";
import SkuBin from "../pages/order-fulfillment/components/order-id/components/sku-bin/SkuBin";
import PickupBin from "../pages/order-fulfillment/components/task-id/components/pickup-bin/PickupBin";
import PickupTote from "../pages/material-inward/components/pickup-tote/PickupTote";
import OrderDetails from "../pages/order-fulfillment/components/task-id/components/order-details/OrderDetails";
import Decommission from "../pages/decommission/Decommission";
import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type MouseEvent,
} from "react";
import axios from "../common/api/axios";
import {
  InwardStatusContext,
  useInwardStatus,
  useInwardStatusContext,
} from "../common/hooks/useInwardStatus";
import {
  OperatorTab,
  resolveDisabledTabTitle,
  resolveEnabledTabs,
  resolveRedirectPath,
} from "../common/utils/operatorTabs";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../app/app-insights";
import CycleCount from "../pages/cycle-count/CycleCount";
import { ReactComponent as CycleCountIcon } from "./icons/cycle-count.svg";

function determineIsReady(task: any): boolean {
  if (!task) {
    return false;
  }

  if (typeof task.isReady === "boolean") {
    return task.isReady;
  }

  if (
    task.decommissionBinTask &&
    typeof task.decommissionBinTask.isReady === "boolean"
  ) {
    return task.decommissionBinTask.isReady;
  }

  if (task.purgeBinTask && typeof task.purgeBinTask.isReady === "boolean") {
    return task.purgeBinTask.isReady;
  }

  return false;
}

function isActiveOrderToteMappingTask(task: any): boolean {
  return (
    !!task?.id &&
    !!task?.orderToteMappingTask &&
    task?.isComplete !== true &&
    task?.isCancelled !== true &&
    task?.isAborted !== true
  );
}

function AppShell() {
  const { Header, Content } = Layout;
  const audioPlayer: any = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    activeOperationMode,
    isInflightInward,
    loading: orchestrationLoading,
  } = useInwardStatusContext();
  const [orderPickCount, setOrderPickCount] = useState(0);
  const [orderToteMappingCount, setOrderToteMappingCount] = useState(0);
  const [inwardCount, setInwardCount] = useState(0);
  const [topupCount, setTopupCount] = useState(0);
  const [topupData, setTopupData] = useState<any[]>([]);
  const [inwardData, setInwardData] = useState<any[]>([]);
  const [orderData, setOrderData] = useState<any[]>([]);
  const [decommissionCount, setDecommissionCount] = useState(0);
  const [decommissionData, setDecommissionData] = useState<any[]>([]);
  const [cycleCountCount, setCycleCountCount] = useState(0);
  const [configName, setConfigName] = useState<string | undefined>(undefined);
  const completedDecommissionIdsRef = useRef<Set<string>>(new Set());
  const [isLoadingDecommission, setIsLoadingDecommission] = useState(false);
  const [decommissionLoaded, setDecommissionLoaded] = useState(false);
  const duration: any = process.env.REACT_APP_REFRESH_DURATION;
  const orderCount = orderPickCount + orderToteMappingCount;
  const onHandleSignOutClick = () => {
    window.location.replace("https://carteplus-ui-landing-test.azureedge.net/");
  };
  const playAudio = useCallback(() => {
    if (audioPlayer.current) {
      audioPlayer.current.play().catch(() => {
        // Browser blocked autoplay — user hasn't interacted with the page yet
      });
    }
  }, []);
  const getPickItemsData = useCallback(async () => {
    try {
      const res = await axios.get("PickStation/pickItems", {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setOrderData(res.data);
        setOrderPickCount((prevCount) => {
          if (res.data.length !== prevCount) {
            playAudio();
          }
          return res.data.length;
        });
      }
    } catch (_error) {
      // ignore
    }
  }, [playAudio]);

  const getOrderToteMappingTasks = useCallback(async () => {
    try {
      const res = await axios.get("PickStation/orderToteMappingTasks", {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });

      if (res.status === 200 && Array.isArray(res.data)) {
        const activeCount = res.data.filter(isActiveOrderToteMappingTask).length;
        setOrderToteMappingCount((prevCount) => {
          if (activeCount !== prevCount) {
            playAudio();
          }
          return activeCount;
        });
      }
    } catch (_error) {
      setOrderToteMappingCount(0);
    }
  }, [playAudio]);

  const getPickTopupData = useCallback(async () => {
    try {
      const res = await axios.get("PickStation/replenishTasks", {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setTopupData(res.data);
        setTopupCount((prevCount) => {
          if (res.data.length !== prevCount) {
            playAudio();
          }
          return res.data.length;
        });
      }
    } catch (_error) {
      // ignore
    }
  }, [playAudio]);

  const getPickInwardData = useCallback(async () => {
    try {
      const res = await axios.get("PickStation/inwardTasks", {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setInwardData(res.data);
        setInwardCount((prevCount) => {
          if (res.data.length !== prevCount) {
            playAudio();
          }
          return res.data.length;
        });
      }
    } catch (_error) {
      // ignore
    }
  }, [playAudio]);

  useEffect(() => {
    let isMounted = true;
    const fetchConfig = async () => {
      try {
        const res = await axios.get("/Configuration/config", {
          headers: {
            Accept: "application/json",
            tenantId: 1,
          },
        });
        if (isMounted) {
          const nextConfig =
            res.status === 200 && typeof res.data?.configName === "string"
              ? res.data.configName
              : "";
          setConfigName(nextConfig);
        }
      } catch (_error) {
        if (isMounted) {
          setConfigName("");
        }
      }
    };
    fetchConfig();
    return () => {
      isMounted = false;
    };
  }, []);

  const getCycleCountData = useCallback(async () => {
    try {
      const res = await axios.get("PickStation/cycleCountTasks", {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setCycleCountCount((prevCount) => {
          if (res.data.length !== prevCount) {
            playAudio();
          }
          return res.data.length;
        });
      }
    } catch (_error) {
      // ignore
    }
  }, [playAudio]);

  const getDecommissionTasks = useCallback(async () => {
    setIsLoadingDecommission(true);
    const headers = {
      Accept: "application/json",
      tenantId: 1,
    };

    try {
      const [decommissionResult, purgeResult] = await Promise.allSettled([
        axios.get("PickStation/decommissionBinTasks", { headers }),
        axios.get("PickStation/purgeBinTasks", { headers }),
      ]);

      const mergedTasks: any[] = [];

      if (
        decommissionResult.status === "fulfilled" &&
        Array.isArray(decommissionResult.value.data)
      ) {
        mergedTasks.push(
          ...decommissionResult.value.data.map((task: any) => ({
            ...task,
            taskType: "decommission",
            isReady: determineIsReady(task),
          }))
        );
      }

      if (purgeResult.status === "fulfilled" && Array.isArray(purgeResult.value.data)) {
        mergedTasks.push(
          ...purgeResult.value.data.map((task: any) => ({
            ...task,
            taskType: "purge",
            isReady: determineIsReady(task),
          }))
        );
      }

      setDecommissionData(mergedTasks);

      setDecommissionCount((prevCount) => {
        const activeCount = mergedTasks.filter((task: any) => {
          if (!determineIsReady(task)) {
            return false;
          }

          const taskId =
            task?.id ?? task?.decommissionBinTask?.binId ?? task?.purgeBinTask?.binId;

          if (!taskId) {
            return false;
          }

          return !completedDecommissionIdsRef.current.has(taskId);
        }).length;

        if (activeCount !== prevCount) {
          playAudio();
        }

        return activeCount;
      });
    } catch (_error) {
      setDecommissionData([]);
      setDecommissionCount(0);
    } finally {
      setIsLoadingDecommission(false);
      setDecommissionLoaded(true);
    }
  }, [playAudio]);

  const fetchLatestData = useCallback(() => {
    getPickItemsData();
    getPickTopupData();
    getPickInwardData();
    getOrderToteMappingTasks();
    getDecommissionTasks();
    getCycleCountData();
  }, [
    getDecommissionTasks,
    getOrderToteMappingTasks,
    getPickInwardData,
    getPickItemsData,
    getPickTopupData,
    getCycleCountData,
  ]);

  const handleCompletedDecommission = useCallback((taskId: string) => {
    completedDecommissionIdsRef.current.add(taskId);
    setDecommissionCount((current) => Math.max(0, current - 1));
  }, []);

  useEffect(() => {
    const refreshInterval = Number(duration) > 0 ? Number(duration) : 5000;

    fetchLatestData();
    const interval = setInterval(fetchLatestData, refreshInterval);
    return () => {
      clearInterval(interval);
    };
  }, [duration, fetchLatestData]);

  // A tote waiting to be decommissioned or purged is real operator work whether or not it has
  // reached its gate yet, so the tab opens on the task existing — the badge, which counts only
  // tasks that are ready to action, stays as it was.
  const hasDecommissionWork = decommissionData.length > 0;
  const gatingReady = !orchestrationLoading && decommissionLoaded;

  const tabGating = useMemo(
    () => ({ gatingReady, activeOperationMode, isInflightInward, hasDecommissionWork }),
    [gatingReady, activeOperationMode, isInflightInward, hasDecommissionWork]
  );
  const enabledTabs = useMemo(() => resolveEnabledTabs(tabGating), [tabGating]);

  // Disabling a tab the operator is already standing on would strand them, so the same gating
  // decision that greys the link also moves them off it.
  useEffect(() => {
    const redirectPath = resolveRedirectPath(location.pathname, enabledTabs);
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [enabledTabs, location.pathname, navigate]);

  const tabLinkProps = useCallback(
    (tab: OperatorTab) =>
      enabledTabs.includes(tab)
        ? {}
        : {
            $disabled: true,
            "aria-disabled": true,
            tabIndex: -1,
            title: resolveDisabledTabTitle(tab, tabGating),
            onClick: (event: MouseEvent<HTMLAnchorElement>) => event.preventDefault(),
          },
    [enabledTabs, tabGating]
  );

  // remaining code

  const current = new Date();
  const date = format(current, "MMM dd, yyyy");

  const time = current.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const items = [
    {
      key: "1",
      label: (
        <div>
          <a className="sign-out" onClick={onHandleSignOutClick}>
            Sign Out
          </a>
        </div>
      ),
    },
  ];
  /*  useEffect(() => {
    const testInterval = setInterval(() => {
      //const out:any = inwardData;
      //const out:any = topupData;
      const out:any = orderData;
      if(samData.length>out.length)
      {
        out.push(samData[out.length])
        setOrderData(out);
        setOrderCount(out.length);
        //setTopupData(out);
        //setTopupCount(out.length);
        //setInwardData(out);
        //setInwardCount(out.length)
      }
    }, 3000)
    return () => {
      clearInterval(testInterval);
    };
  }, []) */

  return (
    <Layout>
      <Header>
        <Styles.Container>
          <Styles.Left>
            <Styles.HeaderLogo />
            <audio
              ref={audioPlayer}
              src={require("./icons/notification-sound.mp3")}
              muted={false}
              autoPlay={true}
            />
            <Styles.NavContainer>
              <Styles.NLink
                to="/order-id"
                {...tabLinkProps("orderFulfillment")}
                className={
                  location?.pathname === "/order_details/task_id" ||
                    location?.pathname === "/task-id" ||
                    location?.pathname === "/order-id" ||
                    location?.pathname === "/pickup_bin/task_id" ||
                    location?.pathname === "/sku_bin/order_id" ||
                    location?.pathname === "/"
                    ? "active"
                    : "none"
                }
              >
                <OrderIcon />
                <Styles.Badge>
                  Order <br /> Fulfillment
                  {orderCount > 0 && <span>{orderCount}</span>}
                </Styles.Badge>
              </Styles.NLink>
              <Styles.NLink
                to="/material-inward"
                {...tabLinkProps("materialInward")}
                className={
                  location?.pathname === "/pickup_tote" ? "active" : "none"
                }
              >
                <MaterialIcon />
                <Styles.Badge>
                  Material <br /> Inwards
                  {inwardCount > 0 && <span>{inwardCount}</span>}
                </Styles.Badge>
              </Styles.NLink>
              <Styles.NLink
                to="/material-topup"
                {...tabLinkProps("materialReplenish")}
                className={
                  location?.pathname === "/drop_details" ||
                    location?.pathname === "/"
                    ? "active"
                    : "none"
                }
              >
                <MaterialPopupIcon />
                <Styles.Badge>
                  Material <br /> Replenish
                  {topupCount > 0 && <span>{topupCount}</span>}
                </Styles.Badge>
              </Styles.NLink>
              <Styles.NLink
                to="/decommission"
                {...tabLinkProps("decommission")}
                className={
                  location?.pathname?.startsWith("/decommission") ? "active" : "none"
                }
              >
                <DecommissionIcon />
                <Styles.Badge>
                  Decommission
                  {decommissionCount > 0 && <span>{decommissionCount}</span>}
                </Styles.Badge>
              </Styles.NLink>
              <Styles.NLink
                to="/cycle-count"
                {...tabLinkProps("cycleCount")}
                className={
                  location?.pathname?.startsWith("/cycle-count") ? "active" : "none"
                }
              >
                <CycleCountIcon />
                <Styles.Badge>
                  Cycle <br /> Count
                  {cycleCountCount > 0 && <span>{cycleCountCount}</span>}
                </Styles.Badge>
              </Styles.NLink>
            </Styles.NavContainer>
          </Styles.Left>
          <Styles.Right>
            <Styles.DateContainer>
              <Styles.Time>{time}</Styles.Time>
              <Styles.Date>{date}</Styles.Date>
            </Styles.DateContainer>
            {/* <Dropdown
              className="dropdown"
              menu={{
                items,
              }}
              placement="bottomRight"
              overlayClassName="header-dropdown"
            >
              <Button>
                <Styles.UserImage src={User}/>
                <Styles.UserContainer>
                  <Styles.UserName>John Doe</Styles.UserName>
                  <Styles.Role>Operator</Styles.Role>
                </Styles.UserContainer>
              </Button>
            </Dropdown> */}
          </Styles.Right>
        </Styles.Container>
      </Header>
      <AppInsightsContext.Provider value={reactPlugin}>
        <Routes>
          <Route path="/" element={<OrderFulfillment />}>
            <Route index element={<Navigate to="order-id" />} />
            <Route path="task-id" element={<TaskId />} />
            <Route
              path="order-id"
              element={
                <OrderId
                  taskCount={orderCount}
                  taskDetails={orderData}
                  configName={configName}
                />
              }
            />
          </Route>
          <Route path="/sku_bin/*" element={<SkuBin />} />
          {/* <Route path="/sku_bin_details/*" element={<OrderDetailNew />} /> */}

          <Route path="/drop_details/*" element={<MaterialOrderDetail />} />

          <Route path="/pickup_bin/*" element={<PickupBin />} />
          <Route path="/order_details/*" element={<OrderDetails />} />
          <Route
            path="/material-inward"
            element={
              <MaterialInward
                taskCount={inwardCount}
                taskDetails={inwardData}
                configName={configName}
              />
            }
          />
          <Route
            path="/pickup_tote/*"
            element={<PickupTote configName={configName} />}
          />
          <Route
            path="/material-topup"
            element={
              <MaterialTopup
                taskCount={topupCount}
                taskDetails={topupData}
                configName={configName}
              />
            }
          />
          <Route
            path="/decommission"
            element={
              <Decommission
                tasks={decommissionData}
                isLoading={isLoadingDecommission}
                refreshTasks={getDecommissionTasks}
                onTaskCompleted={handleCompletedDecommission}
              />
            }
          />
          <Route
            path="/cycle-count"
            element={
              <CycleCount configName={configName} />
            }
          />
          <Route path="*" element={<OrderFulfillment />} />
        </Routes>
      </AppInsightsContext.Provider>
      <Content className="site-layout">
        <Outlet />
      </Content>
    </Layout>
  );
}

// The inward status is polled once here and published to the whole tree, so the nav lock in
// AppShell and the Material Inward screen can never disagree about the same server state.
function App() {
  const inwardStatus = useInwardStatus();

  return (
    <InwardStatusContext.Provider value={inwardStatus}>
      <AppShell />
    </InwardStatusContext.Provider>
  );
}

export default App;
